import { useEffect } from 'react';

type Modifier = 'ctrl' | 'meta' | 'shift' | 'alt';

interface Props {
  onKeyPress: (event: KeyboardEvent) => void;
  key: KeyboardEvent['key'];
  modifiers?: Modifier[];
  enabled?: boolean;
  preventDefault?: boolean;
}

export const useKeyPressListener = ({
  onKeyPress,
  key,
  modifiers = [],
  enabled = true,
  preventDefault = false,
}: Props) => {
  useEffect(() => {
    if (!enabled) return;

    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;

      if (isInputOrEditableContent(target)) {
        return;
      }

      const modifierState = {
        ctrl: event.ctrlKey,
        meta: event.metaKey,
        shift: event.shiftKey,
        alt: event.altKey,
      };

      const modifierMatched =
        modifiers.length === 0 || modifiers.some(m => modifierState[m]);

      if (event.key === key && modifierMatched) {
        if (preventDefault) event.preventDefault();
        onKeyPress(event);
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [key, modifiers, onKeyPress, enabled, preventDefault]);
};

const isInputOrEditableContent = (target: HTMLElement | null) => {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target?.isContentEditable
  );
};
